import { useMemo } from "react";
import { Pageable } from "../../../services/pageable";
import { getFields, validationFields } from "../../common/models/fields";
import {
  FraudReportAttachments,
  FraudReportSocialMedia,
  FraudReportTransaction,
  FundsTypeEnum,
  ReportingOptionEnum,
  TokenLifecycleEnum,
  TypeOfFraudEnum,
} from "./internal";

namespace FraudReport {
  export interface T {
    action: string;
    address: string;
    attachments?: FraudReportAttachments[];
    blockchain: string;
    createdAt: string;
    description: string;
    fraudAmount: number;
    fraudCurrency: string;
    fraudType: typeof TypeOfFraudEnum.T;
    fundsType: typeof FundsTypeEnum.T;
    id: string;
    otherFraudDescription?: string;
    reportType: typeof ReportingOptionEnum.T;
    requireHelp: boolean;
    socialMedia: FraudReportSocialMedia[];
    tokenLifecycle: typeof TokenLifecycleEnum.T;
    transactions: FraudReportTransaction[];
  }
  export const useFields = () => {
    const addressSchema = validationFields.address.useSchema();

    const actionName = validationFields.action.useName();
    const actionSchema = validationFields.action.useSchema();
    const addressName = validationFields.address.useName();
    const attachmentsName = validationFields.attachments.useName();
    const attachmentsSchema = validationFields.attachments.useSchema();
    const blockchainName = validationFields.blockchain.useName();
    const blockchainSchema = validationFields.blockchain.useSchema();
    const createdAtName = validationFields.createdDate.useName();
    const createdAtSchema = validationFields.createdDate.useSchema();
    const descriptionName = validationFields.description.useName();
    const descriptionSchema = validationFields.description.useSchema();
    const fraudAmountName = validationFields.fraudAmount.useName();
    const fraudAmountSchema = validationFields.fraudAmount.useSchema();
    const fraudCurrencyName = validationFields.fraudCurrency.useName();
    const fraudCurrencySchema = validationFields.fraudCurrency.useSchema();
    const fraudTypeName = validationFields.fraudType.useName();
    const fraudTypeSchema = validationFields.fraudType.useSchema();
    const fundsTypeName = validationFields.fundsType.useName();
    const fundsTypeSchema = validationFields.fundsType.useSchema();
    const idName = validationFields.id.useName();
    const idSchema = validationFields.id.useSchema();
    const otherFraudDescriptionName = validationFields.otherFraudDescription.useName();
    const otherFraudDescriptionSchema = validationFields.otherFraudDescription.useSchema();
    const reportTypeName = validationFields.reportType.useName();
    const reportTypeSchema = validationFields.reportType.useSchema();
    const requireHelpName = validationFields.requireHelp.useName();
    const requireHelpSchema = validationFields.requireHelp.useSchema();
    const socialMediaName = validationFields.socialMedia.useName();
    const socialMediaSchema = validationFields.socialMedia.useSchema();
    const tokenLifecycleName = validationFields.tokenLifecycle.useName();
    const tokenLifecycleSchema = validationFields.tokenLifecycle.useSchema();
    const transactionsName = validationFields.transactions.useName();
    const transactionsSchema = validationFields.transactions.useSchema();

    return useMemo(
      () =>
        getFields({
          action: {
            name: actionName,
            schema: actionSchema,
          },
          address: {
            name: addressName,
            schema: addressSchema,
          },
          attachments: {
            name: attachmentsName,
            schema: attachmentsSchema,
          },
          blockchain: {
            name: blockchainName,
            schema: blockchainSchema,
          },
          createdAt: {
            name: createdAtName,
            schema: createdAtSchema,
          },
          description: {
            name: descriptionName,
            schema: descriptionSchema,
          },
          fraudAmount: {
            name: fraudAmountName,
            schema: fraudAmountSchema,
          },
          fraudCurrency: {
            name: fraudCurrencyName,
            schema: fraudCurrencySchema,
          },
          fraudType: {
            name: fraudTypeName,
            schema: fraudTypeSchema,
          },
          fundsType: {
            name: fundsTypeName,
            schema: fundsTypeSchema,
          },
          id: {
            name: idName,
            schema: idSchema,
          },
          otherFraudDescription: {
            name: otherFraudDescriptionName,
            schema: otherFraudDescriptionSchema,
          },
          reportType: {
            name: reportTypeName,
            schema: reportTypeSchema,
          },
          requireHelp: {
            name: requireHelpName,
            schema: requireHelpSchema,
          },
          socialMedia: {
            name: socialMediaName,
            schema: socialMediaSchema,
          },
          tokenLifecycle: {
            name: tokenLifecycleName,
            schema: tokenLifecycleSchema,
          },
          transactions: {
            name: transactionsName,
            schema: transactionsSchema,
          },
        }),
      [
        actionName,
        actionSchema,
        addressName,
        addressSchema,
        attachmentsName,
        attachmentsSchema,
        blockchainName,
        blockchainSchema,
        createdAtName,
        createdAtSchema,
        descriptionName,
        descriptionSchema,
        fraudAmountName,
        fraudAmountSchema,
        fraudCurrencyName,
        fraudCurrencySchema,
        fraudTypeName,
        fraudTypeSchema,
        fundsTypeName,
        fundsTypeSchema,
        idName,
        idSchema,
        otherFraudDescriptionName,
        otherFraudDescriptionSchema,
        reportTypeName,
        reportTypeSchema,
        requireHelpName,
        requireHelpSchema,
        socialMediaName,
        socialMediaSchema,
        tokenLifecycleName,
        tokenLifecycleSchema,
        transactionsName,
        transactionsSchema,
      ]
    );
  };

  export const defaults: Partial<T> = {
    socialMedia: [],
  };
}

export namespace CryptoDTO {
  export namespace Responses {
    export namespace GetFraudReportSummary {
      export interface T {
        totalFromBeginningOfTime: number;
        maxPerWeek: number;
        totalPerWeek: { date: string; count: number }[];
      }

      export const defaults: T = {
        totalFromBeginningOfTime: 0,
        maxPerWeek: 0,
        totalPerWeek: [],
      };
    }

    export namespace FraudReportCreated {
      export interface T {
        id: string;
      }

      export const useFields = () => {
        const idName = validationFields.id.useName();
        const idSchema = validationFields.id.useSchema();

        return useMemo(
          () =>
            getFields({
              id: {
                name: idName,
                schema: idSchema,
              },
            }),
          [idName, idSchema]
        );
      };

      export const defaults: Partial<T> = {};
    }

    export namespace GetSingleFraudReport {
      export type T = FraudReport.T;
      export const useFields = FraudReport.useFields;
      export const defaults = FraudReport.defaults;
    }

    export namespace GetAllFraudReport {
      export interface T {
        content: FraudReport.T[];
        pageable: Pageable.Response.T;
      }
      export const defaults: T = {
        content: [],
        pageable: Pageable.Response.defaults,
      };
    }
    export namespace UploadDocument {
      export interface T {}
      export const defaults: T = {};
    }
  }

  export namespace Requests {
    export namespace CreateFraudReport {
      export type T = Omit<FraudReport.T, "id" | "createdAt">;
      export const useFields = FraudReport.useFields;
      export const defaults: T = {
        reportType: "IDENTIFIED_CLIENT",
        description: "",
        action: "",
        blockchain: "ETH",
        address: "",
        fraudType: "HACKED_WALLET_SEEDS",
        fraudAmount: 0.0,
        tokenLifecycle: "LAUNCH",
        requireHelp: false,
        fraudCurrency: "",
        fundsType: "PERSONAL",
        transactions: [],
        socialMedia: [],
        attachments: [],
      };
    }

    export namespace UploadDocument {
      export interface T {
        reportId: string;
        files: FileList;
      }
      export const useFields = () => {
        const reportIdName = validationFields.id.useName();
        const reportIdSchema = validationFields.id.useSchema();
        const filesName = validationFields.attachments.useName();
        const filesSchema = validationFields.attachments.useSchema();

        return useMemo(
          () =>
            getFields({
              reportId: {
                name: reportIdName,
                schema: reportIdSchema,
              },
              files: {
                name: filesName,
                schema: filesSchema,
              },
            }),
          [reportIdName, reportIdSchema, filesName, filesSchema]
        );
      };

      export const defaults: Partial<T> = {};
    }
  }
}
